export const CHECKOUT_URL = 'https://anatomy-ready.impultienda.ar/checkout'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/8LZqwMzFV6jA19E7reVfKn/8581599d-535a-4af1-a173-8032d8d35d49.webp'

export const BOX_IMAGE_ALT =
  'ANATOMY READY™ — complete 6-in-1 box set of Anatomy & Physiology teaching volumes'

export interface PainPoint {
  title: string
  body: string[]
  emphasis?: string[]
}

export const painPoints: PainPoint[] = [
  {
    title: 'One topic. Too many materials to prepare.',
    body: [
      'The skeletal system. The nervous system. The heart. The lungs. Cells. Tissues. Digestion. Homeostasis.',
      'You know the topic—but finding the right diagram, worksheet, activity, review material, and assessment for that lesson is the difficult part.',
    ],
  },
  {
    title: 'You know the subject—but what should you prepare next?',
    body: [
      'You can explain organs, tissues, cells, bones, muscles, body systems, anatomical terminology, and physiological processes.',
      'But preparing the class requires something different: knowing what material to use first, what students should practice next, what diagram to show, what worksheet to print, and how to check understanding.',
    ],
  },
  {
    title: 'Your teaching resources are everywhere',
    body: [
      'Textbooks. Anatomy diagrams. Worksheets. Videos. PDFs. Teacher resources. Websites. Old lesson files.',
      'The information exists—but it’s scattered across different resources and rarely organized around clear, structured Anatomy & Physiology teaching.',
    ],
    emphasis: [
      'You don’t need more scattered information.',
      'You need one organized teaching system.',
    ],
  },
  {
    title: 'Finding the first worksheet doesn’t mean the lesson is ready',
    body: [
      'Searching through websites, opening old files, adapting diagrams, creating activities, writing questions, preparing reviews, and trying to determine what students actually need for the lesson.',
      'Then the next topic requires another search.',
    ],
    emphasis: [
      'Your teaching system should help you organize the class—not just give you more disconnected resources.',
    ],
  },
]

export const teachingFlow: string[] = ['Teach', 'Show', 'Practice', 'Review']

export interface Volume {
  label: string
  title: string
  description: string
  color: string
}

export const volumes: Volume[] = [
  {
    label: 'Volume 1',
    title: 'Foundations of Human Anatomy & Physiology™',
    description:
      'Build the foundation for Anatomy & Physiology with anatomical terminology, levels of organization, body systems, anatomical position, directional terms, body planes, body cavities, cells, membrane transport, tissues, homeostasis, and system integration.',
    color: '#1e3a8a',
  },
  {
    label: 'Volume 2',
    title: 'Skin, Bones, Joints & Muscles™',
    description:
      'Teach the integumentary system, skin structure, skeletal anatomy, major bones, bone structure, joints, joint movements, skeletal muscle, major muscle groups, muscle contraction, and musculoskeletal relationships.',
    color: '#15803d',
  },
  {
    label: 'Volume 3',
    title: 'Nervous System, Senses & Endocrine™',
    description:
      'Teach neuron structure, the central and peripheral nervous systems, brain anatomy, spinal cord, reflexes, vision, hearing, special senses, endocrine glands, hormones, feedback regulation, and nervous-endocrine relationships.',
    color: '#6d28d9',
  },
  {
    label: 'Volume 4',
    title: 'Blood, Heart, Circulation & Immunity™',
    description:
      'Teach blood components, heart anatomy, blood flow, cardiac function, arteries, veins, capillaries, pulmonary circulation, systemic circulation, the lymphatic system, immunity, and cardiovascular relationships.',
    color: '#b91c1c',
  },
  {
    label: 'Volume 5',
    title: 'Respiratory, Digestive & Metabolism™',
    description:
      'Teach respiratory anatomy, breathing, gas exchange, digestive anatomy, digestion, absorption, accessory organs, nutrients, metabolism, ATP, and the relationship between oxygen, nutrients, and cellular activity.',
    color: '#c2410c',
  },
  {
    label: 'Volume 6',
    title: 'Urinary, Reproductive & Body Integration™',
    description:
      'Teach urinary anatomy, kidney structure, nephrons, filtration, reabsorption, secretion, fluid balance, male and female reproductive anatomy, human development, homeostasis, body-system connections, and whole-body integration.',
    color: '#0e7490',
  },
]

export interface Slide {
  title: string
  caption: string
  image: string
}

export const slides: Slide[] = [
  {
    title: 'Interior page: Skeletal system worksheet — major bone labeling',
    caption: 'Skeletal system worksheet — major bone labeling',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b67d9373-d80f-4ef1-97cb-1445b2ac7741.jpg',
  },
  {
    title: 'Interior page: Heart anatomy activity — chambers and blood flow',
    caption: 'Heart anatomy activity — chambers and blood flow',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c34e713f-3083-46bb-80db-dea5a5bf2fdc.jpg',
  },
  {
    title: 'Interior page: Human cell — organelles and structure',
    caption: 'Human cell — organelles and structure',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/071717cd-9b61-4b20-afef-bbceebc33bbd.jpg',
  },
  {
    title: 'Interior page: Body systems connections — visual system relationships',
    caption: 'Body systems connections — visual system relationships',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/46a5e240-d965-4e0a-b7fe-27c76391a692.jpg',
  },
  {
    title: 'Interior page: Homeostasis — feedback and regulation',
    caption: 'Homeostasis — feedback and regulation',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fcc372e0-5d04-412d-9620-b40afa1cf979.jpg',
  },
]

export interface Bonus {
  label: string
  title: string
  description: string
}

export const bonuses: Bonus[] = [
  {
    label: 'Bonus 1',
    title: 'The Complete Anatomy & Physiology Yearly Planner™',
    description:
      'Organize the school year with a 36-week Anatomy & Physiology roadmap, unit pacing, weekly planning, assessment organization, mastery tracking, reteach planning, and course progression.',
  },
  {
    label: 'Bonus 2',
    title: '100 Anatomy & Physiology Bell Ringers™',
    description:
      'Use ready-to-use classroom warm-ups with anatomy labeling, visual questions, matching activities, structure-and-function exercises, system review, quick scenarios, and short student challenges.',
  },
  {
    label: 'Bonus 3',
    title: '150 Anatomy & Physiology Vocabulary Cards™',
    description:
      'Review essential Anatomy & Physiology terminology with visual vocabulary cards for structures, systems, functions, anatomical directions, cells, tissues, organs, and physiological processes.',
  },
  {
    label: 'Bonus 4',
    title: 'The Complete Anatomy & Physiology Assessment Pack™',
    description:
      'Organize student assessment with ready-to-use quizzes, diagram checks, unit assessments, application questions, final review, answer keys, and mastery tracking.',
  },
  {
    label: 'Bonus 5',
    title: 'The Human Body Visual Atlas™',
    description:
      'Use labeled anatomical diagrams, body-system posters, student labeling maps, organ illustrations, cell and tissue visuals, and whole-body system maps throughout the course.',
  },
]

export interface Differentiator {
  title: string
  description: string
}

export const differentiators: Differentiator[] = [
  {
    title: 'Practical visual resources',
    description:
      'Find the anatomical structure, body system, diagram, activity, worksheet, assessment, or review resource you need while teaching Anatomy & Physiology.',
  },
  {
    title: 'Structured teaching system',
    description:
      'Move from foundational concepts and anatomical terminology to cells, tissues, organs, body systems, visual practice, review, and assessment.',
  },
  {
    title: '6 teaching areas',
    description:
      'Foundations, skin and movement systems, nervous and endocrine systems, cardiovascular and immune systems, respiratory and digestive systems, urinary and reproductive systems, and whole-body integration.',
  },
  {
    title: 'Ready to use again and again',
    description:
      'Return to the system whenever you need to explain, show, label, practice, review, or assess an Anatomy & Physiology topic.',
  },
]

export const offerChecklist: string[] = [
  '6 complete volumes + 5 premium bonuses',
  'Every major human body system',
  'Visual diagrams & labeling activities',
  'Worksheets, assessments & answer keys',
  'A full school year, already organized',
]

export interface Testimonial {
  quote: string
  name: string
  role: string
  location: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'I’ve taught Anatomy & Physiology for twelve years, and my problem was never the content—it was the preparation time. ANATOMY READY™ finally gave me a clear structure. Now I open the unit, print the activity, and walk into class knowing exactly what we’re doing.',
    name: 'Melissa Carter, 39',
    role: 'High School Anatomy & Physiology Teacher',
    location: 'Columbus, Ohio, United States',
  },
  {
    quote:
      'The visuals made the biggest difference. Blood flow, neurons, and the nephron get confusing fast with only paragraphs. Having diagrams, labeling activities, and worksheets together means I’m no longer spending my evenings searching for resources that work together.',
    name: 'Jason Miller, 34',
    role: 'Biology & Anatomy Teacher',
    location: 'Charlotte, North Carolina, United States',
  },
  {
    quote:
      'A&P used to be my most time-consuming class to prepare. ANATOMY READY™ isn’t just another folder of random worksheets—the units follow a logical progression. The yearly planner and assessments remove the small tasks that used to add up every week.',
    name: 'Rachel Thompson, 42',
    role: 'High School Science Teacher',
    location: 'Calgary, Alberta, Canada',
  },
  {
    quote:
      'I used to adapt materials from different websites that never matched. Now every resource is organized around the same topics, so the class feels consistent. Less time putting materials together, more time thinking about how to teach.',
    name: 'Daniel Brooks, 46',
    role: 'Health Science & Anatomy Teacher',
    location: 'Manchester, United Kingdom',
  },
  {
    quote:
      'I introduce a body system with the visuals, give a labeling activity, review with vocabulary cards, and move into the assessment—without creating anything separately. For a subject with this much content, that organization is incredibly valuable.',
    name: 'Sarah Mitchell, 37',
    role: 'Senior Secondary Biology & Human Anatomy Teacher',
    location: 'Brisbane, Queensland, Australia',
  },
]

export const finalSteps: string[] = [
  'Find the topic.',
  'Open the resource.',
  'Use the visual.',
  'Teach the lesson.',
]

export const finalFlow: string[] = ['Teach', 'Show', 'Practice', 'Review', 'Assess']

export interface FooterLink {
  label: string
  href: string
}

export const footerLinks: FooterLink[] = [
  { label: 'Terms & Conditions', href: '#' },
  { label: 'Privacy Policy', href: '#' },
  { label: 'Contact Support', href: '#' },
]

export interface PurchaseNotice {
  name: string
  city: string
  minutesAgo: number
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Jessica M.', city: 'Austin, TX', minutesAgo: 3 },
  { name: 'Michael T.', city: 'Columbus, OH', minutesAgo: 8 },
  { name: 'Sarah L.', city: 'Denver, CO', minutesAgo: 14 },
  { name: 'David B.', city: 'Atlanta, GA', minutesAgo: 21 },
  { name: 'Emily R.', city: 'Phoenix, AZ', minutesAgo: 27 },
  { name: 'Chris P.', city: 'Charlotte, NC', minutesAgo: 35 },
  { name: 'Amanda K.', city: 'Portland, OR', minutesAgo: 42 },
  { name: 'Jason W.', city: 'Nashville, TN', minutesAgo: 51 },
]

export interface Faq {
  question: string
  answer: string[]
}

export const faqs: Faq[] = [
  {
    question: 'Who is this system for?',
    answer: [
      'High School Anatomy & Physiology teachers, Biology teachers, Health Science teachers, Biomedical Science teachers, homeschool educators, and educators teaching human Anatomy & Physiology.',
    ],
  },
  {
    question: 'Is this a physical product?',
    answer: [
      'No. ANATOMY READY™ is a digital resource. You receive digital materials that can be viewed on compatible devices and printed when needed.',
    ],
  },
  {
    question: 'Is this a training course or certification?',
    answer: [
      'No. ANATOMY READY™ is a teaching-resource system. It is not a certification program.',
    ],
  },
  {
    question: 'Is it another theory-heavy ebook?',
    answer: [
      'No. The system is built around visual teaching resources, diagrams, worksheets, classroom activities, assessments, answer keys, and organized Anatomy & Physiology material.',
    ],
  },
  {
    question: 'What areas are included?',
    answer: [
      'The system covers Anatomy & Physiology foundations, anatomical terminology, cells, tissues, skin, skeletal and muscular systems, nervous and endocrine systems, blood and circulation, immunity, respiration, digestion, metabolism, urinary function, reproductive anatomy, homeostasis, and body-system integration.',
    ],
  },
  {
    question: 'Does this replace my school curriculum requirements?',
    answer: [
      'ANATOMY READY™ is an educational teaching resource. Teachers should use it according to their school, district, state, or applicable curriculum requirements.',
    ],
  },
  {
    question: 'How will I receive it?',
    answer: ['You receive digital access after purchase.'],
  },
  {
    question: 'Does it work for High School Anatomy & Physiology?',
    answer: [
      'Yes. ANATOMY READY™ is designed around High School Anatomy & Physiology teaching.',
    ],
  },
  {
    question: 'Do I need special software to use it?',
    answer: [
      'No specialized teaching software is required to use standard PDF resources.',
    ],
  },
  {
    question: 'Can I use it on a computer or tablet during class?',
    answer: [
      'Yes. The digital resources can be viewed on compatible devices and materials can also be printed for classroom use.',
    ],
  },
]
